import React, { ChangeEvent } from 'react';
import { useStyles, cx } from '../../../lib/styles';
import { Brand, Color } from '../../../lib/theme';

import inputStyles from './Input_styles';
import InputLabel from './InputLabel';
import InputStatus, { InputValidationStatus } from './InputStatus';

export interface InputProps {
  /** The brand to use for colours. */
  brand?: Brand;

  /** Any additional classnames to include on the input element. */
  className?: string;

  /** Any additional classnames to include on the label element. */
  labelClassName?: string;

  /** Any additional classnames to include on the container element. */
  containerClassName?: string;

  /** The HTML5 input type. */
  type?: string;

  /**
   * The color of the border of the input.
   *
   * @default 'grey'
   */
  border?: Color;

  /** Whether the input should be marked as required (for browser autofill purposes). */
  required?: boolean;

  /** The ARIA label for the input. */
  ariaLabel?: string;

  /** The label to display above the input. This will also be used as the ARIA label if an override isn't provided. */
  label?: string;

  /** The MIME types to accept if the `type` is set to `file`. */
  accept?: string;

  /** The current value for the input. */
  value?: any;

  /** The name of the input. */
  name?: string;

  /** The validation status of the input. Either `good` or `bad`. */
  status?: InputValidationStatus;

  /** The message to show along with the validation status (if set). */
  statusMessage?: string;

  /** The event listener for when the value of the input changes. */
  onChange?: (value: any, event: ChangeEvent<HTMLInputElement>) => any;

  /** The event listener for when the input is blurred. */
  onBlur?: () => any;

  /** The event listener for when the input is focused. */
  onFocus?: () => any;

  /** The placeholder for the input. */
  placeholder?: string;

  /** Whether the input should start focused. */
  autoFocus?: boolean;

  /** Whether the input should be disabled and allow values being entered. */
  disabled?: boolean;

  /** Minimum date selection allowed (for date pickers). */
  min?: string;

  /** Maximum date selection allowed (for date pickers). */
  max?: string;
}

/**
 * A text input.
 */
const Input = (props: InputProps) => {
  const {
    type = 'text',
    value = '',
    required,
    ariaLabel,
    label,
    name,
    className,
    labelClassName,
    containerClassName,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    autoFocus,
    disabled,
    status,
    statusMessage,
    min,
    max,
    accept,
  } = props;

  const styles = useStyles(inputStyles(props));
  const Elem = type === 'textarea' ? 'textarea' : 'input';

  return (
    <div className={containerClassName}>
      {label && (
        <InputLabel
          className={labelClassName}
          required={required}
          status={status}
          htmlFor={name}
        >
          {label}
        </InputLabel>
      )}

      <Elem
        type={type}
        value={value}
        name={name}
        aria-label={ariaLabel || label}
        required={required}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        onChange={event => onChange && onChange(event.target.value, event)}
        onBlur={onBlur}
        onFocus={onFocus}
        min={min}
        max={max}
        accept={accept}
        className={cx('Input', styles.container, className)}
      />

      {status && statusMessage && (
        <InputStatus message={statusMessage} status={status} />
      )}
    </div>
  );
};

export default Input;
